import { useState, useMemo, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle
} from '@/components/ui/card';
import {
    Plus,
    Search,
    Building,
    Edit,
    User,
    Mail,
    Phone,
    Trash2
} from 'lucide-react';
import api from '@/lib/api';
import AddOrganizationModal from '@/components/Modals/AddOrganizationModal';
import EditContactsModal from '@/components/Modals/EditContactsModal';

import { getContacts } from '@/store/contactSlice';
import { createOrganization, deleteOrganization } from '@/api/organizations';


const ContactRow = ({ contact }) => (
    <Card className="p-3">
        <div className="font-medium flex items-center">
            <User className="mr-2 h-4 w-4" />
            {contact.full_name?.trim() || contact.email?.split('@')[0] || 'Unnamed'}
        </div>
        <div className="text-sm text-muted-foreground ml-6">
            {contact.title || 'No title'}
        </div>
        <div className="text-sm text-muted-foreground ml-6 flex items-center">
            <Mail className="mr-1 h-3 w-3" />
            {contact.email || 'No email'}
        </div>
        {contact.phone && (
            <div className="text-sm text-muted-foreground ml-6 flex items-center">
                <Phone className="mr-1 h-3 w-3" />
                {contact.phone}
            </div>
        )}
    </Card>
);



const OrganizationPage = () => {
    const dispatch = useDispatch();
    const { organizations, status } = useSelector((state) => state.contacts);

    const [orgSearchTerm, setOrgSearchTerm] = useState('');
    const [showAddModal, setShowAddModal] = useState(false);
    const [editingOrg, setEditingOrg] = useState(null);


    const [purgeLoading, setPurgeLoading] = useState(false);
    const [purgeErr, setPurgeErr] = useState(null);

    const handlePurgeUnassigned = async () => {
        try {
          setPurgeLoading(true);
          setPurgeErr(null);
      
          console.debug('[purge] dry-run start');
          // dry-run ТЕМ ЖЕ методом DELETE, но с ?dry_run=1
          const dry = await api.delete('/api/contacts/purge-unassigned-empty/', {
            params: { dry_run: 1 },
          });
          console.debug('[purge] dry-run resp', dry?.status, dry?.data);
      
          const count = Number(dry?.data?.would_delete ?? 0);
          if (!Number.isFinite(count)) {
            throw new Error('Unexpected dry-run response shape');
          }
          if (count === 0) {
            console.info('[purge] nothing to delete');
            return;
          }
      
          const ok = window.confirm(
            `Будут удалены ${count} пустых непривязанных контактов. Продолжить?`
          );
          if (!ok) return;
      
          console.debug('[purge] delete start');
          const del = await api.delete('/api/contacts/purge-unassigned-empty/');
          console.debug('[purge] delete resp', del?.status, del?.data);
      
          await fetchUnassigned();
          dispatch(getContacts());
        } catch (e) {
          const msg =
            e?.response?.data?.detail ||
            e?.response?.data?.error ||
            e?.message ||
            'Failed to purge unassigned contacts';
          console.error('[purge] error', e?.response?.status, e?.response?.data || e);
          setPurgeErr(msg);
        } finally {
          setPurgeLoading(false);
        }
      };

    const [newOrganization, setNewOrganization] = useState({
        name: '',
        contacts: [{ full_name: '', email: '', phone: '', title: '' }]
    });

    useEffect(() => {
        dispatch(getContacts());
    }, [dispatch]);
    // локальный стейт для непривязанных
    const [unassigned, setUnassigned] = useState([]);
    const [unassignedLoading, setUnassignedLoading] = useState(false);
    const [unassignedErr, setUnassignedErr] = useState(null);

    // загрузка непривязанных контактов
    const fetchUnassigned = async () => {
        try {
            setUnassignedLoading(true);
            setUnassignedErr(null);
            const res = await api.get('/api/contacts/contacts/?unassigned=1'); // ← самый безопасный дефолт
            setUnassigned(res.data || []);
        } catch (e) {
            setUnassignedErr(e?.message || 'Failed to load unassigned contacts');
            setUnassigned([]);
        } finally {
            setUnassignedLoading(false);
        }
    };

    useEffect(() => {
        fetchUnassigned();
    }, []);


    const handleExcelUpload = async (event) => {
        const file = event.target.files[0];
        if (!file) return;

        const formData = new FormData();
        formData.append('file', file);

        try {
            const response = await api.post('/api/contacts/upload/', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            });

            dispatch(getContacts()); // обновить список
            fetchUnassigned(); // обновить и непривязанные
        } catch (error) {
            console.error('❌ Ошибка загрузки Excel:', error);
        }
    };

    const handleAddOrganization = async () => {
        try {
            const cleanedPayload = {
                name: newOrganization.name,
                contacts: newOrganization.contacts.map(({ full_name, email, phone, title }) => ({
                    full_name,
                    email,
                    phone,
                    title
                }))
            };
            await createOrganization(cleanedPayload);
            dispatch(getContacts()); // или fetchOrganizations(), если ты его импортируешь отдельно
            fetchUnassigned();
            setNewOrganization({ name: '', contacts: [] });
            setShowAddModal(false);
        } catch (err) {
            console.error('❌ Ошибка добавления организации:', err);
        }
    };

    const handleDeleteOrganization = async (orgId) => {
        try {
            await deleteOrganization(orgId);
            dispatch(getContacts()); // Обновляем список после удаления
            fetchUnassigned();
        } catch (error) {
            console.error('❌ Ошибка удаления организации:', error);
        }
    };


    const filteredOrganizations = useMemo(() => {
        const term = orgSearchTerm.toLowerCase();
        return organizations.filter((org) => {
            const orgMatch = org.name.toLowerCase().includes(term);
            const contactMatch = org.contacts?.some(
                (c) =>
                    c.full_name?.toLowerCase().includes(term) ||
                    c.email?.toLowerCase().includes(term) ||
                    c.title?.toLowerCase().includes(term)
            );
            return orgMatch || contactMatch;
        });
    }, [orgSearchTerm, organizations]);
    return (
        <div className="space-y-6">

            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-4">
                <h2 className="text-3xl font-bold">Organizations</h2>

                <div className="flex flex-wrap gap-2">
                    <Button onClick={() => setShowAddModal(true)}>
                        <Plus className="mr-2 h-4 w-4" /> Add New Organization
                    </Button>
                    <Button
                        variant="secondary"
                        onClick={() => document.getElementById('excel-upload').click()}
                    >
                        Upload from Excel
                    </Button>
                    <input
                        id="excel-upload"
                        type="file"
                        accept=".xlsx,.xls"
                        style={{ display: 'none' }}
                        onChange={handleExcelUpload}
                    />
                </div>
            </div>

            <div className="relative mb-4">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                    placeholder="Search organizations or contacts..."
                    className="pl-9"
                    value={orgSearchTerm}
                    onChange={(e) => setOrgSearchTerm(e.target.value)}
                />
            </div>

            <div className="space-y-4">
                {status === 'loading' ? (
                    <p className="text-center text-muted-foreground">Loading...</p>
                ) : filteredOrganizations.length === 0 ? (
                    <p className="text-center text-muted-foreground">
                        No organizations found. Click "Add New Organization" to get started.
                    </p>
                ) : (
                    filteredOrganizations.map((org) => (
                        <Card key={org.id}>
                            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                                <CardTitle className="text-2xl font-bold flex items-center">
                                    <Building className="mr-2 h-5 w-5" /> {org.name}
                                </CardTitle>
                                <div className="flex gap-2">
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => setEditingOrg(org)}
                                    >
                                        <Edit className="h-4 w-4" />
                                    </Button>
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => handleDeleteOrganization(org.id)}
                                        className="text-red-500 hover:text-red-700"
                                    >
                                        <Trash2 className="h-4 w-4" />
                                    </Button>
                                </div>
                            </CardHeader>
                            <CardContent>
                                <CardDescription className="mb-3">
                                    {org.contacts?.length || 0} Contacts
                                </CardDescription>
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                    {(org.contacts ?? []).map((c) => (
                                        <ContactRow key={c.id} contact={c} />
                                    ))}
                                </div>
                            </CardContent>
                        </Card>
                    ))
                )}
            </div>

            <Card>
  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
    <CardTitle className="text-2xl font-bold">Unassigned contacts</CardTitle>
    <div className="flex gap-2">
      <Button
        variant="secondary"
        size="sm"
        onClick={fetchUnassigned}
        disabled={unassignedLoading || purgeLoading}
      >
        {unassignedLoading ? 'Refreshing…' : 'Refresh'}
      </Button>
      <Button
        variant="destructive"
        size="sm"
        onClick={handlePurgeUnassigned}
        disabled={unassignedLoading || purgeLoading}
        title="Delete empty unassigned contacts"
      >
        {purgeLoading ? 'Purging…' : 'Purge'}
      </Button>
    </div>
  </CardHeader>

  <CardContent>
    {purgeErr && <p className="text-red-600 mb-2">Error: {purgeErr}</p>}

    {unassignedLoading ? (
      <p className="text-muted-foreground">Loading…</p>
    ) : unassignedErr ? (
      <p className="text-red-600">Error: {unassignedErr}</p>
    ) : (unassigned?.length || 0) === 0 ? (
      <p className="text-muted-foreground">No unassigned contacts 🎉</p>
    ) : (
      <>
        <CardDescription className="mb-3">
          {(unassigned?.length || 0)} Contacts
        </CardDescription>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {unassigned.map((c) => (
            <ContactRow key={c.id} contact={c} />
          ))}
        </div>
      </>
    )}
  </CardContent>
</Card>

            <AddOrganizationModal
                isOpen={showAddModal}
                onClose={() => setShowAddModal(false)}
                newOrganization={newOrganization}
                setNewOrganization={setNewOrganization}
                onAddOrganization={async () => {
                    await handleAddOrganization();
                    setShowAddModal(false);
                }}
            />

            <EditContactsModal
                isOpen={!!editingOrg}
                onClose={() => setEditingOrg(null)}
                organization={editingOrg}
                organizations={organizations}
                setOrganizations={(newOrgs) => {
                    // Обновим org и закроем модалку
                    setEditingOrg(null);
                    dispatch(getContacts()); // обновим данные из API
                    fetchUnassigned();
                }}
            />
        </div>
    );
};

export default OrganizationPage;